import { z } from 'zod';

import { imageQuery, imageSchema, type MediaItemSizeEnum } from './image';
import { videoQuery, videoSchema } from './video';

export const mediaQuery = ({
  imageSize = 'MEDIUM_LARGE',
}: {
  imageSize: MediaItemSizeEnum;
}) => /* graphql */ `
  media {
    type

    image {
      ${imageQuery(imageSize)}
    }

    video {
      ${videoQuery({ thumbnailSize: 'MEDIUM_LARGE' })}
    }

    youtubeId
    vimeoId

    ratio {
      width
      height
    }
  }
`;

export const builtinMediaQuery = ({
  imageSize = 'MEDIUM_LARGE',
}: {
  imageSize: MediaItemSizeEnum;
}) => /* graphql */ `
  builtinMedia {
    type

    image {
      ${imageQuery(imageSize)}
    }

    video {
      ${videoQuery({ thumbnailSize: 'MEDIUM_LARGE' })}
    }
  }
`;

const ratioSchema = z.object({
  width: z.coerce.number(),
  height: z.coerce.number(),
});

export const mediaSchema = z.object({
  media: z.discriminatedUnion('type', [
    z.object({
      type: z.literal('image'),
      image: imageSchema,
    }),

    z.object({
      type: z.literal('video'),
      video: videoSchema,
    }),

    z.object({
      type: z.literal('youtube'),
      youtubeId: z.string(),
      ratio: ratioSchema.nullish(),
    }),

    z.object({
      type: z.literal('vimeo'),
      vimeoId: z.string(),
      ratio: ratioSchema.nullish(),
    }),
  ]),
});

export const optionalMediaSchema = z.object({
  media: z.discriminatedUnion('type', [
    z.object({
      type: z.literal('image'),
      image: imageSchema.nullish(),
    }),

    z.object({
      type: z.literal('video'),
      video: videoSchema.nullish(),
    }),

    z.object({
      type: z.literal('youtube'),
      youtubeId: z.string().nullish(),
      ratio: ratioSchema.nullish(),
    }),

    z.object({
      type: z.literal('vimeo'),
      vimeoId: z.string().nullish(),
      ratio: ratioSchema.nullish(),
    }),
  ]),
});

export const builtinMediaSchema = z.object({
  builtinMedia: z.discriminatedUnion('type', [
    z.object({
      type: z.literal('image'),
      image: imageSchema,
    }),

    z.object({
      type: z.literal('video'),
      video: videoSchema,
    }),
  ]),
});

export const optionalBuiltinMediaSchema = z.object({
  builtinMedia: z.discriminatedUnion('type', [
    z.object({
      type: z.literal('image'),
      image: imageSchema.nullish(),
    }),

    z.object({
      type: z.literal('video'),
      video: videoSchema.nullish(),
    }),
  ]),
});

export type MediaSchema = z.infer<typeof mediaSchema>;
export type OptionalMediaSchema = z.infer<typeof optionalMediaSchema>;

export type BuiltinMediaSchema = z.infer<typeof builtinMediaSchema>;
export type OptionalBuiltinMediaSchema = z.infer<
  typeof optionalBuiltinMediaSchema
>;
